/**
 * Counterparty Store - Zustand State Management
 * 
 * Manages counterparty (tiers) state.
 */

import { create } from 'zustand';
import type {
  Counterparty,
  CreateCounterpartyRequest,
  UpdateCounterpartyRequest,
  MergeCounterpartyResponse,
} from '@budgira/shared';
import { counterpartyService } from '../services/counterparty.service';
import i18n from '../i18n';

interface CounterpartyState {
  counterparties: Counterparty[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCounterparties: () => Promise<void>;
  createCounterparty: (data: CreateCounterpartyRequest) => Promise<Counterparty | null>;
  updateCounterparty: (id: string, data: UpdateCounterpartyRequest) => Promise<Counterparty | null>;
  deleteCounterparty: (id: string) => Promise<boolean>;
  purgeUnused: () => Promise<number>;
  getOperationsCount: (id: string, accountId?: string) => Promise<number>;
  getUniqueCategory: (id: string) => Promise<{ id: string; name: string; logoUrl: string | null } | null>;
  getAccountsForCounterparty: (id: string) => Promise<{ id: string; name: string; source?: string }[]>;
  getOperationTypes: (id: string, accountId?: string) => Promise<{ hasDebits: boolean; hasCredits: boolean }>;
  updateBulkCategory: (counterpartyId: string, categoryId: string, accountId?: string, operationType?: 'debit' | 'credit') => Promise<number>;
  mergeCounterparties: (targetId: string, sourceId: string, accountId?: string, operationType?: 'debit' | 'credit') => Promise<MergeCounterpartyResponse | null>;
  renameForAccount: (counterpartyId: string, name: string, logoUrl: string | undefined, accountId: string, operationType?: 'debit' | 'credit') => Promise<{ counterparty: Counterparty; operationsUpdated: number; schedulersUpdated: number } | null>;
  findCounterpartyById: (id: string) => Counterparty | undefined;
  clearError: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeCounterpartyError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;
  
  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }
  
  // Also check for Error instances
  if (err instanceof Error && !errorMessage) {
    errorMessage = err.message;
  }
  
  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    'COUNTERPARTY_NOT_FOUND': 'counterparties.errors.notFound',
    'UNAUTHORIZED': 'counterparties.errors.unauthorized',
    'NAME_ALREADY_EXISTS': 'counterparties.errors.duplicateName',
    'HAS_ASSOCIATED_OPERATIONS': 'counterparties.errors.hasAssociatedOperations',
    'HAS_ASSOCIATED_SCHEDULERS': 'counterparties.errors.hasAssociatedSchedulers',
    'VALIDATION_ERROR': 'counterparties.errors.validation',
  };
  
  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }
  
  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('counterparties.errors.generic'));
}

export const useCounterpartyStore = create<CounterpartyState>((set, get) => ({
  counterparties: [],
  isLoading: false,
  error: null,

  fetchCounterparties: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const counterparties = await counterpartyService.list();
      // Sort alphabetically by name
      const sorted = [...counterparties].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      set({ counterparties: sorted, isLoading: false });
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
    }
  },

  createCounterparty: async (data: CreateCounterpartyRequest): Promise<Counterparty | null> => {
    set({ isLoading: true, error: null });
    try {
      const counterparty = await counterpartyService.create(data);
      // Refresh the list
      await get().fetchCounterparties();
      return counterparty;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateCounterparty: async (id: string, data: UpdateCounterpartyRequest): Promise<Counterparty | null> => {
    set({ isLoading: true, error: null });
    try {
      const counterparty = await counterpartyService.update(id, data);
      // Refresh the list
      await get().fetchCounterparties();
      return counterparty;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteCounterparty: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      await counterpartyService.delete(id);
      // Refresh the list
      await get().fetchCounterparties();
      return true;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  purgeUnused: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const count = await counterpartyService.purgeUnused();
      await get().fetchCounterparties();
      return count;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  getOperationsCount: async (id: string, accountId?: string): Promise<number> => {
    try {
      return await counterpartyService.getOperationsCount(id, accountId);
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message });
      return 0;
    }
  },

  getUniqueCategory: async (id: string): Promise<{ id: string; name: string; logoUrl: string | null } | null> => {
    try {
      return await counterpartyService.getUniqueCategory(id);
    } catch (err) {
      // Silent fail - not critical
      return null;
    }
  },

  getAccountsForCounterparty: async (id: string): Promise<{ id: string; name: string; source?: string }[]> => {
    try {
      return await counterpartyService.getAccounts(id);
    } catch (err) {
      // Silent fail - not critical
      return [];
    }
  },

  getOperationTypes: async (id: string, accountId?: string): Promise<{ hasDebits: boolean; hasCredits: boolean }> => {
    try {
      return await counterpartyService.getOperationTypes(id, accountId);
    } catch (err) {
      // Silent fail - default to having both types
      return { hasDebits: true, hasCredits: true };
    }
  },

  updateBulkCategory: async (counterpartyId: string, categoryId: string, accountId?: string, operationType?: 'debit' | 'credit'): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const count = await counterpartyService.updateBulkCategory(counterpartyId, categoryId, accountId, operationType);
      set({ isLoading: false });
      return count;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  mergeCounterparties: async (targetId: string, sourceId: string, accountId?: string, operationType?: 'debit' | 'credit'): Promise<MergeCounterpartyResponse | null> => {
    set({ isLoading: true, error: null });
    try {
      const result = await counterpartyService.merge(targetId, sourceId, accountId, operationType);
      // Refresh the list after merge
      await get().fetchCounterparties();
      return result;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  renameForAccount: async (
    counterpartyId: string,
    name: string,
    logoUrl: string | undefined,
    accountId: string,
    operationType?: 'debit' | 'credit'
  ): Promise<{ counterparty: Counterparty; operationsUpdated: number; schedulersUpdated: number } | null> => {
    set({ isLoading: true, error: null });
    try {
      const result = await counterpartyService.renameForAccount(counterpartyId, name, logoUrl, accountId, operationType);
      // Refresh the list after rename
      await get().fetchCounterparties();
      return result;
    } catch (err) {
      const message = localizeCounterpartyError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  findCounterpartyById: (id: string): Counterparty | undefined => {
    return get().counterparties.find((c) => c.id === id);
  },

  clearError: () => set({ error: null }),
}));

export default useCounterpartyStore;
